import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Award, ArrowRight, X, Share2, Download, ExternalLink } from "lucide-react";
import EnrollmentCertificate from "./EnrollmentCertificate";
import { Scaled } from "./CertificateCard";
import { eliteConfetti } from "../../lib/eliteConfetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D4AF37";
export const CERT_REVEAL_VERSION = "v1";

// One-time "your Enrollment Certificate is ready" reveal for enrolled players.
export default function CertificateReveal({ token, playerId, suppress }) {
  const [cert, setCert] = useState(null);
  const [open, setOpen] = useState(false);
  const [sealed, setSealed] = useState(true);
  const key = playerId ? `nexto_cert_reveal_${CERT_REVEAL_VERSION}_${playerId}` : null;

  useEffect(() => {
    if (!token || !key || localStorage.getItem(key)) return;
    axios.get(`${API}/certificate/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (!data.reveal_seen) setCert(data); })
      .catch(() => {});
  }, [token, key]);

  useEffect(() => {
    if (!cert || suppress) return;
    const t = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(t);
  }, [cert, suppress]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { setSealed(false); eliteConfetti(); }, 1100);
    return () => clearTimeout(t);
  }, [open]);

  const dismiss = () => {
    if (key) localStorage.setItem(key, "1");
    setOpen(false);
    axios.post(`${API}/certificate/me/reveal-ack`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };
  const jump = () => {
    dismiss();
    setTimeout(() => {
      const el = document.querySelector("[data-testid='certificate-card']");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("cert-highlight");
      setTimeout(() => el.classList.remove("cert-highlight"), 3200);
    }, 250);
  };

  if (!cert) return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="certificate-reveal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-md" onClick={dismiss}>
          <motion.div initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }} onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg rounded-2xl overflow-hidden elite-carbon-bg"
            style={{ border: `1px solid ${GOLD}88`, boxShadow: `0 0 0 1px rgba(0,0,0,.6), 0 0 80px ${GOLD}33, 0 40px 100px rgba(0,0,0,.8)` }}>
            <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(600px 260px at 50% -10%, ${GOLD}2e, transparent 70%)` }} />
            <button data-testid="certificate-reveal-close" onClick={dismiss} aria-label="Close" className="absolute top-3 right-3 z-20 text-white/50 hover:text-white p-2"><X size={18} /></button>

            <div className="relative px-6 pt-7 pb-4 text-center">
              <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: GOLD }} /> Official · The Next O Technical Academy
              </div>
              <h2 className="font-headings text-3xl sm:text-4xl uppercase tracking-wide leading-none">Your Certificate<br /><span style={{ color: GOLD }}>Is Ready</span></h2>
              <p className="text-sm text-white/60 mt-3 max-w-sm mx-auto">{cert.player_name} is officially enrolled in <b className="text-white">{cert.volume}</b>. Download it, frame it, share it — this is your first credential on the road to the Next O Standard.</p>
            </div>

            <div className="relative px-6">
              <div className="relative rounded-lg overflow-hidden" style={{ border: `1px solid ${GOLD}55`, boxShadow: `0 20px 60px rgba(0,0,0,.6)` }}>
                <motion.div animate={{ filter: sealed ? "blur(6px) brightness(.55)" : "blur(0px) brightness(1)" }} transition={{ duration: 0.9, ease: "easeOut" }}>
                  <Scaled><EnrollmentCertificate name={cert.player_name} certId={cert.certificate_id} enrolledAt={cert.enrolled_at} volume={cert.volume} /></Scaled>
                </motion.div>
                <AnimatePresence>
                  {sealed && (
                    <motion.div data-testid="certificate-reveal-seal" initial={{ scale: 0.6, opacity: 0, rotate: -12 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} exit={{ scale: 1.6, opacity: 0, rotate: 8 }}
                      transition={{ type: "spring", damping: 16, stiffness: 220 }} className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{ background: "radial-gradient(circle at 35% 30%, #F0D77A, #B8912E 60%, #7d5f14)", boxShadow: `0 0 40px ${GOLD}aa, inset 0 -6px 14px rgba(0,0,0,.4)` }}>
                        <Award size={40} className="text-[#1a1200]" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <motion.div aria-hidden initial={{ x: "-120%" }} animate={{ x: sealed ? "-120%" : "140%" }} transition={{ duration: 1.1, delay: 0.15, ease: "easeInOut" }}
                  className="absolute inset-y-0 w-1/3 pointer-events-none" style={{ background: "linear-gradient(100deg, transparent, rgba(255,255,255,.35), transparent)" }} />
              </div>
              <div className="flex items-center justify-center gap-3 mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                <span>№ {cert.certificate_id}</span><span className="w-1 h-1 rounded-full bg-white/30" /><span className="inline-flex items-center gap-1"><Download size={10} /> PNG</span><span className="inline-flex items-center gap-1"><Share2 size={10} /> Share</span>
              </div>
            </div>

            <div className="relative px-6 pb-6 pt-5 space-y-2.5">
              <button data-testid="certificate-reveal-jump" onClick={jump}
                className="cta-glow w-full inline-flex items-center justify-center gap-2 text-black font-subheadings font-bold uppercase tracking-wider text-sm py-3.5 rounded-lg transition-transform active:scale-[0.98]"
                style={{ background: `linear-gradient(90deg, #F0D77A, ${GOLD} 45%, #B8912E)` }}>
                <Award size={16} /> Download &amp; Share My Certificate <ArrowRight size={15} />
              </button>
              <div className="flex items-center justify-between">
                <a data-testid="certificate-reveal-view" href={`/certificate/${cert.certificate_id}`} target="_blank" rel="noreferrer" onClick={dismiss}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-white/50 hover:text-white py-1"><ExternalLink size={12} /> Open full-size</a>
                <button data-testid="certificate-reveal-later" onClick={dismiss} className="text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white py-1">Later</button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
